import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { toast } from "react-toastify";
import { setGeneratedStops, nextStep, previousStep } from "../../../redux/routeFLowSlice";
import styles from "./SplitStopsStep.module.scss";

const mapContainerStyle = {
    width: "100%",
    height: "500px",
};

const defaultCenter = {
    lat: 25.7617,
    lng: -80.1918,
};

const libraries = ["places"];

function SplitStopsStep({ deliveryId }) {
    const dispatch = useDispatch();
    const generatedStops = useSelector((state) => state.routeFlowSlice.generatedStops);
    const [showSplitDialog, setShowSplitDialog] = useState(false);
    const [selectedStop, setSelectedStop] = useState(null);
    const [selectedPackages, setSelectedPackages] = useState([]);
    const [newLocation, setNewLocation] = useState(null);
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [isSelectingLocation, setIsSelectingLocation] = useState(false);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries,
    });

    const stopsWithMultiplePackages = generatedStops.filter((stop) => stop.packages.length > 1);

    const handleSplitClick = (stop) => {
        setSelectedStop(stop);
        setSelectedPackages([]);
        setNewLocation(null);
        setIsSelectingLocation(false);
        setMapCenter({
            lat: stop.latitude || defaultCenter.lat,
            lng: stop.longitude || defaultCenter.lng,
        });
        setShowSplitDialog(true);
    };

    const handlePackageSelection = (pkg) => {
        setSelectedPackages((prev) => {
            const exists = prev.find((p) => p.Waybill === pkg.Waybill);
            if (exists) {
                return prev.filter((p) => p.Waybill !== pkg.Waybill);
            } else {
                // Can't select all packages
                if (prev.length >= selectedStop.packages.length - 1) {
                    toast.warning("At least one package must remain at the original location");
                    return prev;
                }
                return [...prev, pkg];
            }
        });
    };

    const handleMapClick = (event) => {
        if (!isSelectingLocation) {
            toast.info("Click 'Select Location on Map' button first");
            return;
        }

        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        setNewLocation({ lat, lng });
        setIsSelectingLocation(false);
        toast.success("New location selected!");
    };

    const handleSplitConfirm = () => {
        if (selectedPackages.length === 0) {
            toast.error("Please select at least one package to split");
            return;
        }

        if (!newLocation) {
            toast.error("Please select a new location on the map");
            return;
        }

        // Create deep copies of all stops to avoid mutation
        const allStops = generatedStops.map((stop) => ({
            ...stop,
            packages: [...stop.packages], // Also copy the packages array
        }));

        // Find the index of the stop being split
        const originalStopIndex = allStops.findIndex((stop) => stop.stopId === selectedStop.stopId);

        if (originalStopIndex === -1) {
            toast.error("Original stop not found");
            return;
        }

        // Calculate remaining packages (packages NOT selected for the new stop)
        const remainingPackages = selectedStop.packages.filter((pkg) => !selectedPackages.find((sp) => sp.Waybill === pkg.Waybill));

        // Update original stop with remaining packages only
        allStops[originalStopIndex] = {
            ...allStops[originalStopIndex],
            packages: remainingPackages,
        };

        // Create new stop with selected packages and new location
        const newStop = {
            stopId: `STOP-SPLIT-${Date.now()}`,
            stopNumber: allStops.length + 1, // Temporary number
            clientId: selectedStop.clientId,
            clientName: selectedStop.clientName,
            latitude: newLocation.lat,
            longitude: newLocation.lng,
            status: "Not Delivered",
            packages: [...selectedPackages], // Copy the array
            address: `Split from Stop #${selectedStop.stopNumber}`,
            email: selectedStop.email || "",
            phone: selectedStop.phone || "",
            isSplit: true,
            originalStopId: selectedStop.stopId,
        };

        // Add the new stop to the array
        allStops.push(newStop);

        // Re-number all stops sequentially (create new objects to avoid mutation)
        const renumberedStops = allStops.map((stop, index) => ({
            ...stop,
            stopNumber: index + 1,
        }));

        // Update Redux state with all stops
        dispatch(setGeneratedStops(renumberedStops));

        toast.success(`Stop split successfully! Created new stop with ${selectedPackages.length} package(s). Original stop now has ${remainingPackages.length} package(s).`);

        // Reset and close dialog
        setShowSplitDialog(false);
        setSelectedStop(null);
        setSelectedPackages([]);
        setNewLocation(null);
        setIsSelectingLocation(false);
    };

    const handleNext = () => {
        dispatch(nextStep());
    };

    const handleBack = () => {
        dispatch(previousStep());
    };

    const handleCancelSplit = () => {
        setShowSplitDialog(false);
        setSelectedStop(null);
        setSelectedPackages([]);
        setNewLocation(null);
        setIsSelectingLocation(false);
    };

    if (!isLoaded) {
        return (
            <div className={styles.loadingContainer}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: "3rem" }}></i>
                <p>Loading map...</p>
            </div>
        );
    }

    return (
        <div className={styles.splitStopsContainer}>
            <div className={styles.header}>
                <h2>Split Stops</h2>
                <p>Split stops with multiple packages to different delivery locations</p>
            </div>

            {stopsWithMultiplePackages.length === 0 ? (
                <div className={styles.emptyState}>
                    <i className="pi pi-check-circle" style={{ fontSize: "3rem", color: "#27ae60" }}></i>
                    <p>No stops need splitting</p>
                    <small>All stops have single packages or you can proceed to optimization</small>
                </div>
            ) : (
                <div className={styles.splitableStops}>
                    <div className={styles.infoBox}>
                        <i className="pi pi-info-circle"></i>
                        <span>{stopsWithMultiplePackages.length} stop(s) with multiple packages can be split</span>
                    </div>

                    <div className={styles.stopsGrid}>
                        {stopsWithMultiplePackages.map((stop) => (
                            <Card key={stop.stopId} className={styles.stopCard}>
                                <div className={styles.stopHeader}>
                                    <span className={styles.stopNumber}>Stop #{stop.stopNumber}</span>
                                    <span className={styles.packageCount}>{stop.packages.length} packages</span>
                                </div>

                                <div className={styles.stopBody}>
                                    <div className={styles.clientName}>
                                        <i className="pi pi-user"></i>
                                        <span>{stop.clientName}</span>
                                    </div>

                                    <div className={styles.address}>
                                        <i className="pi pi-map-marker"></i>
                                        <span>{stop.address}</span>
                                    </div>

                                    <div className={styles.packageList}>
                                        {stop.packages.map((pkg, idx) => (
                                            <div key={idx} className={styles.packageChip}>
                                                {pkg.Waybill}
                                            </div>
                                        ))}
                                    </div>

                                    <Button label="Split This Stop" icon="pi pi-directions" className="p-button-sm" onClick={() => handleSplitClick(stop)} />
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {/* Show All Current Stops Summary */}
            <div className={styles.allStopsSummary}>
                <h3>Current Stops Summary</h3>
                <div className={styles.summaryCards}>
                    {generatedStops.map((stop) => (
                        <div key={stop.stopId} className={styles.miniCard}>
                            <div className={styles.miniCardHeader}>
                                <span>Stop #{stop.stopNumber}</span>
                                {stop.isSplit && <span className={styles.splitBadge}>Split</span>}
                            </div>
                            <div className={styles.miniCardBody}>
                                <small>{stop.clientName}</small>
                                <small>{stop.packages.length} package(s)</small>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Navigation */}
            <div className={styles.navigation}>
                <Button label="Back" icon="pi pi-arrow-left" onClick={handleBack} className="p-button-outlined" />
                <Button label="Next: Optimize Route" icon="pi pi-arrow-right" iconPos="right" onClick={handleNext} />
            </div>

            {/* Split Dialog */}
            <Dialog visible={showSplitDialog} onHide={handleCancelSplit} header={`Split Stop #${selectedStop?.stopNumber} - ${selectedStop?.clientName}`} modal className={styles.splitDialog} style={{ width: "900px", maxWidth: "95vw" }}>
                {selectedStop && (
                    <div className={styles.splitContent}>
                        {/* Instructions */}
                        <div className={styles.instructions}>
                            <div className={styles.instructionStep}>
                                <span className={styles.stepNumber}>1</span>
                                <span>Select packages to move to new location</span>
                            </div>
                            <div className={styles.instructionStep}>
                                <span className={styles.stepNumber}>2</span>
                                <span>Click on map to set new delivery location</span>
                            </div>
                            <div className={styles.instructionStep}>
                                <span className={styles.stepNumber}>3</span>
                                <span>Confirm split to create new stop</span>
                            </div>
                        </div>

                        {/* Package Selection */}
                        <div className={styles.section}>
                            <h4>
                                <i className="pi pi-box"></i> Select Packages to Move
                            </h4>
                            <p className={styles.hint}>Choose packages for the new delivery location</p>

                            <div className={styles.packageSelection}>
                                {selectedStop.packages.map((pkg) => (
                                    <div key={pkg.Waybill} className={styles.packageCheckbox}>
                                        <Checkbox inputId={pkg.Waybill} checked={selectedPackages.some((p) => p.Waybill === pkg.Waybill)} onChange={() => handlePackageSelection(pkg)} />
                                        <label htmlFor={pkg.Waybill}>
                                            <span className={styles.waybill}>{pkg.Waybill}</span>
                                            <span className={styles.description}>{pkg.description || "No description"}</span>
                                        </label>
                                    </div>
                                ))}
                            </div>

                            <div className={styles.selectionSummary}>
                                <div className={styles.summaryItem}>
                                    <i className="pi pi-arrow-right"></i>
                                    <span>Moving to new location: {selectedPackages.length}</span>
                                </div>
                                <div className={styles.summaryItem}>
                                    <i className="pi pi-home"></i>
                                    <span>Staying at original: {selectedStop.packages.length - selectedPackages.length}</span>
                                </div>
                            </div>
                        </div>

                        {/* Map Selection */}
                        <div className={styles.section}>
                            <h4>
                                <i className="pi pi-map"></i> Set New Delivery Location
                            </h4>
                            <p className={styles.hint}>Click on the map to select the new location</p>

                            <div className={styles.mapControls}>
                                <Button
                                    label={isSelectingLocation ? "Selecting... (Click map)" : "Select Location on Map"}
                                    icon={isSelectingLocation ? "pi pi-spin pi-spinner" : "pi pi-map-marker"}
                                    onClick={() => setIsSelectingLocation(!isSelectingLocation)}
                                    className={isSelectingLocation ? "p-button-success" : "p-button-outlined"}
                                />
                                {newLocation && <Button label="Clear Location" icon="pi pi-times" onClick={() => setNewLocation(null)} className="p-button-outlined p-button-danger" />}
                            </div>

                            <GoogleMap
                                mapContainerStyle={mapContainerStyle}
                                center={mapCenter}
                                zoom={13}
                                onClick={handleMapClick}
                                options={{
                                    streetViewControl: false,
                                    mapTypeControl: false,
                                }}
                            >
                                {/* Original Location */}
                                <Marker
                                    position={{
                                        lat: selectedStop.latitude,
                                        lng: selectedStop.longitude,
                                    }}
                                    label={{
                                        text: "Original",
                                        color: "white",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                    }}
                                    icon={{
                                        url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                                    }}
                                    title={`Original Location: ${selectedStop.address}`}
                                />

                                {/* New Location */}
                                {newLocation && (
                                    <Marker
                                        position={newLocation}
                                        label={{
                                            text: "New",
                                            color: "white",
                                            fontSize: "12px",
                                            fontWeight: "bold",
                                        }}
                                        icon={{
                                            url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                                        }}
                                        title="New Delivery Location"
                                    />
                                )}
                            </GoogleMap>

                            {newLocation && (
                                <div className={styles.coordinates}>
                                    <i className="pi pi-check-circle"></i>
                                    <span>
                                        New Location: {newLocation.lat.toFixed(6)}, {newLocation.lng.toFixed(6)}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Preview */}
                        {selectedPackages.length > 0 && newLocation && (
                            <div className={styles.previewSection}>
                                <h4>
                                    <i className="pi pi-eye"></i> Preview Changes
                                </h4>
                                <div className={styles.previewGrid}>
                                    <div className={styles.previewCard}>
                                        <div className={styles.previewHeader}>Original Stop</div>
                                        <div className={styles.previewBody}>
                                            <p>
                                                <strong>Stop #{selectedStop.stopNumber}</strong>
                                            </p>
                                            <p>{selectedStop.clientName}</p>
                                            <p className={styles.packageCount}>{selectedStop.packages.length - selectedPackages.length} package(s)</p>
                                        </div>
                                    </div>

                                    <div className={styles.previewArrow}>
                                        <i className="pi pi-arrow-right"></i>
                                    </div>

                                    <div className={styles.previewCard}>
                                        <div className={styles.previewHeader}>New Stop</div>
                                        <div className={styles.previewBody}>
                                            <p>
                                                <strong>Stop #{generatedStops.length + 1}</strong>
                                            </p>
                                            <p>{selectedStop.clientName}</p>
                                            <p className={styles.packageCount}>{selectedPackages.length} package(s)</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className={styles.dialogActions}>
                            <Button label="Cancel" icon="pi pi-times" onClick={handleCancelSplit} className="p-button-outlined" />
                            <Button label="Confirm Split" icon="pi pi-check" onClick={handleSplitConfirm} disabled={selectedPackages.length === 0 || !newLocation} className="p-button-success" />
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
}

export default SplitStopsStep;
